import clsx from "clsx";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { alpha, Button, InputBase } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { setSearchResults, setSearchQuery } from "../../../actions";
import { useSearchModal } from "../../../hooks";

const useStyles = makeStyles((theme) => ({
  searchWrap: {
    marginLeft: 10,
    maxWidth: 400,
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(["width", "background-color"], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeOut,
    }),
    display: "flex",
    alignItems: "center",
  },
  searchCollapsed: {
    width: 100,
    backgroundColor: alpha(theme.palette.common.grey, 0.25),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.grey, 0.35),
    },
  },
  searchExpanded: {
    width: 260,
    backgroundColor: alpha(theme.palette.common.grey, 0.35),
  },
  searchIcon: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.palette.component.headerColor,
    minWidth: "unset",
  },
  inputRoot: {
    color: theme.palette.component.headerColor,
    width: "100%",
  },
  inputInput: {
    paddingTop: 11,
    paddingRight: 34,
    paddingBottom: 11,
    paddingLeft: 11,
    transition: theme.transitions.create("width"),
    color: theme.palette.component.headerColor,
    fontSize: 12,
    "&::placeholder": {
      color: theme.palette.common.navbarColor,
      opacity: 1,
    },
  },
  zoomIcon: {
    fontSize: 16,
  },
}));

const HeaderSearch = () => {
  const classes = useStyles();
  const intl = useIntl();
  const search = useSearchModal();
  const dispatch = useDispatch();

  const searchQuery = useSelector(
    (state) => state.ui.toggleDetails.searchQuery
  );
  const selectedWorkspace = useSelector(
    (state) => state.ui.toggleWorkspaces.workspace
  );

  const [focused, setFocused] = useState(false);
  const [searchText, setSearchText] = useState(searchQuery);

  const initiateSearch = () => {
    dispatch(setSearchQuery({ searchQuery: searchText }));
    search.open();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      initiateSearch();
    }
  };

  const workspaceId = selectedWorkspace?.id;

  useEffect(() => {
    dispatch(setSearchResults({}));
  }, [workspaceId, dispatch]);

  const isExpanded = focused || Boolean(searchText);
  const searchLabel = intl.formatMessage({ id: "nav.search" });

  return (
    <div className={classes.searchWrap}>
      <div
        className={clsx(
          classes.search,
          isExpanded ? classes.searchExpanded : classes.searchCollapsed
        )}
      >
        <InputBase
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          inputProps={{ "aria-label": searchLabel }}
          onChange={(event) => setSearchText(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={searchLabel}
          value={searchText}
        />

        <Button
          className={classes.searchIcon}
          onMouseDown={(event) => event.preventDefault()}
          onClick={initiateSearch}
          tabIndex={-1}
        >
          <i className={`nc-icon nc-zoom-split ${classes.zoomIcon}`} />
        </Button>
      </div>
    </div>
  );
};

export default HeaderSearch;
